import { useEffect, useRef, useState } from "react";
import type { NotificationDto } from "@codestack/shared";
import { cn } from "@codestack/ui";
import { apiRequest } from "../lib/api";
import { relativeTime } from "../lib/format";

type NotificationList = {
  items: NotificationDto[];
  unreadCount: number;
};

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<NotificationList | null>(null);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const load = async () => {
    setLoading(true);
    try {
      const result = await apiRequest<NotificationList>("/admin/notifications", {
        query: { limit: 12 },
      });
      setData(result);
    } catch {
      setData({ items: [], unreadCount: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    const interval = window.setInterval(() => void load(), 60_000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!open) return;

    const onClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const markRead = async (id: string) => {
    await apiRequest<void>(`/admin/notifications/${id}/read`, { method: "POST" });
    setData((current) =>
      current
        ? {
            unreadCount: Math.max(0, current.unreadCount - 1),
            items: current.items.map((item) =>
              item.id === id ? { ...item, readAt: new Date().toISOString() } : item,
            ),
          }
        : current,
    );
  };

  const markAll = async () => {
    await apiRequest<void>("/admin/notifications/read-all", { method: "POST" });
    setData((current) =>
      current
        ? {
            unreadCount: 0,
            items: current.items.map((item) => ({
              ...item,
              readAt: item.readAt ?? new Date().toISOString(),
            })),
          }
        : current,
    );
  };

  const unread = data?.unreadCount ?? 0;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-label={`Notifications${unread ? `, ${unread} unread` : ""}`}
        className="relative grid h-9 w-9 place-items-center rounded-lg border border-white/10 text-ink-300 transition-colors hover:text-white"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden="true">
          <path
            d="M6 9a6 6 0 1 1 12 0c0 4 1.5 5.5 1.5 5.5h-15S6 13 6 9Zm3.5 8.5a2.5 2.5 0 0 0 5 0"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {unread > 0 ? (
          <span className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-rose-500 px-1 text-[9px] font-bold text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="absolute right-0 top-11 z-50 w-80 overflow-hidden rounded-xl border border-white/[0.1] bg-ink-900 shadow-lift">
          <div className="flex items-center justify-between border-b border-white/[0.07] px-4 py-3">
            <p className="text-xs font-semibold text-white">Notifications</p>
            {unread > 0 ? (
              <button
                type="button"
                onClick={() => void markAll()}
                className="text-[11px] text-brand-300 hover:text-brand-200"
              >
                Mark all read
              </button>
            ) : null}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {loading && !data ? (
              <p className="px-4 py-6 text-center text-xs text-ink-500">Loading…</p>
            ) : null}

            {data && data.items.length === 0 ? (
              <p className="px-4 py-6 text-center text-xs text-ink-500">
                No notifications yet.
              </p>
            ) : null}

            {data?.items.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  if (!item.readAt) void markRead(item.id);
                }}
                className={cn(
                  "block w-full border-b border-white/[0.05] px-4 py-3 text-left transition-colors last:border-0 hover:bg-white/[0.03]",
                  !item.readAt && "bg-brand-500/[0.04]",
                )}
              >
                <div className="flex items-start gap-2">
                  {!item.readAt ? (
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-400" />
                  ) : (
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-transparent" />
                  )}
                  <div className="min-w-0">
                    <p className="truncate text-xs font-medium text-ink-100">
                      {item.title}
                    </p>
                    <p className="mt-0.5 line-clamp-2 text-[11px] leading-relaxed text-ink-500">
                      {item.body}
                    </p>
                    <p className="mt-1 text-[10px] text-ink-600">
                      {relativeTime(item.createdAt)}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
